'use client';

import { Open, Swap } from '@drift-labs/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import {
	FundingHistoryData,
	fundingHistorySelector,
} from 'src/selectors/fundingHistorySelector';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { sortRecordsByTs } from 'src/utils/events';
import { getUrlForTx } from 'src/utils/explorer';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import TableV2 from '../Tables/TableV2';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useFundingPaymentsHistory } from 'src/hooks/historyData/useFundingPaymentsHistory';
import { UISerializableFundingPaymentRecord } from '@drift/common';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import Link from 'src/components/primitives/Link/Link';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketCellV2 from 'src/components/Tables/cells/MarketCellV2';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import NumLib from 'src/utils/NumLib';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';

const PAGE_SIZE = 20;

const EMPTY_FUNDING_HISTORY_ARRAY: FundingHistoryData[] = [];

const TABLE_GRID =
	'minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(40px,.5fr)';

const HEADERS: {
	label: string;
	tooltipDefinitionId: DefinitionTooltipID | undefined;
}[] = [
	{ label: 'time', tooltipDefinitionId: undefined },
	{ label: 'market', tooltipDefinitionId: undefined },
	{ label: 'direction', tooltipDefinitionId: 'fundingHistoryDirection' },
	{ label: 'position size', tooltipDefinitionId: 'fundingHistoryPositionSize' },
	{ label: 'payment', tooltipDefinitionId: undefined },
	{ label: '', tooltipDefinitionId: undefined },
];

interface V2FundingHistoryTableDisplayProps extends HistoryTableUIProps<UISerializableFundingPaymentRecord> {
	isLive?: boolean;
}

const V2FundingHistoryTableDisplay = (
	props: V2FundingHistoryTableDisplayProps
) => {
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState('funding-payments')
	);
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const currentPage = filters.currentUiPage;

	const currentAccountUserKey = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.userKey
	);
	const currentAccountName = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.name
	);
	const lastUserKey = useRef(currentAccountUserKey ?? '');
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	// Use history data from hook instead of Zustand store
	const { historyData } = props;

	const fundingHistory: FundingHistoryData[] = useMemo(() => {
		if (!historyData.initialHistoryLoaded) {
			return EMPTY_FUNDING_HISTORY_ARRAY;
		}
		return fundingHistorySelector(
			historyData.records,
			currentAccountName ?? ''
		);
	}, [
		historyData.records,
		currentAccountName,
		historyData.initialHistoryLoaded,
	]);

	const filteredFundingPayments = useMemo(
		() =>
			props.filterFn
				? fundingHistory?.filter((record) =>
						props.filterFn(record, filters, { isLive: props.isLive })
					)
				: fundingHistory,
		[fundingHistory, props.filterFn, filters]
	);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? (historyData.totalCount ?? 0) // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredFundingPayments?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	const [currentFundingHistory, setCurrentFundingHistory] = useState(
		fundingHistory?.slice(0, PAGE_SIZE) ?? undefined
	);

	const historyIsLoading = historyData.loading ?? false;

	useEffect(() => {
		setCurrentFundingHistory(
			filteredFundingPayments.slice(
				currentPage * PAGE_SIZE,
				(currentPage + 1) * PAGE_SIZE
			)
		);
	}, [filteredFundingPayments, currentPage]);

	useEffect(() => {
		if (lastUserKey.current !== currentAccountUserKey) {
			resetState();
			lastUserKey.current = currentAccountUserKey ?? '';
		}
	}, [currentAccountUserKey]);

	const resetState = () => {
		setHistoryTableFilterState((s) => {
			s['funding-payments'].currentUiPage = 0;
		});
		setCurrentFundingHistory([]);
	};

	useWalletIsConnected({
		onDisconnectedCallback: resetState,
	});

	const showAccountValues = useShowAccountValues();

	const isMobileScreenSize = useIsMobileScreenSize();

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			currentPage={currentPage}
			pageChangeCallback={props.executePageChange}
			totalItems={maxNumOfRecordsToDisplay}
			loading={historyIsLoading}
			tableState={{
				records: currentFundingHistory,
				id: 'funding_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow grid={TABLE_GRID} lastColumnJustify="start">
					{HEADERS.map(({ label, tooltipDefinitionId }, index) => (
						<TableV2.HeaderCell key={`funding_header_${index}`}>
							{label === 'time' ? (
								<ToggleHeaderCell
									key={`funding_header_${index}`}
									label={label}
									onClick={toggleDateDisplayType}
								/>
							) : (
								<DefinitionTooltip definitionId={tooltipDefinitionId}>
									{label}
								</DefinitionTooltip>
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={sortRecordsByTs(currentFundingHistory)?.map(
				(payment, index) => {
					const key = `${index}_${payment.market}`;
					const side = payment.baseAssetAmount.isNeg() ? 'short' : 'long';
					const size = showAccountValues
						? NumLib.formatNum.toTradePrecisionString(
								payment.baseAssetAmount.toNum(),
								true
							)
						: '∗∗∗∗∗';

					if (isMobileScreenSize) {
						return (
							<MobileTableCard.Root key={key} className="gap-y-lg">
								<MarketCellV2
									className="py-0"
									direction={side}
									marketSymbol={payment.market}
									marketIndex={payment.marketIndex}
									marketType="perp"
									textClass="typo-t2"
									hideLeverage
								/>
								<div className="grid w-full grid-cols-2">
									<MobileTableCard.LabelWithValue
										label="Position Size"
										value={size}
									/>
									<MobileTableCard.LabelWithValue
										className="items-end"
										label="Payment"
										value={
											<NumberDisplay
												value={payment.size}
												type="number_signed"
												suffix="USDC"
												decimals={4}
												isAccountValueToHide
												signDependentColor
												trimZeroes
											/>
										}
									/>
								</div>
								<div className="flex items-center gap-x-xs">
									<MobileTableCard.Label>Time</MobileTableCard.Label>
									<IconButton
										size="sm"
										variant="tertiary"
										className="-m-md p-md"
										onClick={toggleDateDisplayType}
										icon={Swap}
									/>
									<TableV2.DateCell
										className="w-fit py-0 whitespace-nowrap"
										displayAsTimestamp={displayDatesAsTimestamp}
										date={new Date(payment.datetime)}
									/>
								</div>
								<Button
									size="sm"
									variant="secondary"
									as="a"
									href={getUrlForTx(payment.txSig)}
									openInNewTab
								>
									View in scanner
								</Button>
							</MobileTableCard.Root>
						);
					}

					return (
						<TableV2.BodyRow
							key={key}
							className="h-10 border-0"
							grid={TABLE_GRID}
							lastColumnJustify="start"
							isDataRow
						>
							<TableV2.DateCell
								key={`fundingDate_${index}`}
								date={new Date(payment.datetime)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>

							<TableV2.MarketCell
								key={`market_${index}`}
								marketSymbol={`${payment.market}-PERP`}
							/>

							<TableV2.BodyCell
								key={`direction_${index}`}
								className={cj(
									'capitalize',
									side === 'short'
										? 'text-text-negative-red-button'
										: 'text-text-positive-green-button'
								)}
							>
								{side}
							</TableV2.BodyCell>

							<TableV2.BodyCell key={`size_${index}`}>
								{size} {payment.market}
							</TableV2.BodyCell>

							<TableV2.AssetCell
								key={`payment_${index}`}
								className={
									payment.size.ltZero()
										? 'text-text-negative-red-button'
										: 'text-text-positive-green-button'
								}
								value={payment.size}
								suffix="USDC"
								isAccountValueToHide
								disableTooltip
								decimals={4}
								trimZeroes
								semantic
							/>

							<TableV2.BodyCell className="justify-end">
								<Link
									variant="secondary"
									href={getUrlForTx(payment.txSig)}
									rightIcon="none"
									openInNewTab
								>
									<Open size={16} />
								</Link>
							</TableV2.BodyCell>
						</TableV2.BodyRow>
					);
				}
			)}
		/>
	);
};

const V2FundingHistoryTable = (props: { isLive?: boolean }) => {
	const tableState = useFundingPaymentsHistory();

	return <V2FundingHistoryTableDisplay {...tableState} isLive={props.isLive} />;
};

export default V2FundingHistoryTable;
